import type { AssetContainer } from '@babylonjs/core/assetContainer';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import '@babylonjs/loaders/glTF';

import type { Kb3dManager } from '@kb3d/models/classes/kb3d-manager';
import { KbGeometry } from '@kb3d/models/classes/primitives/kb-geometry';
import { MeshService } from '@kb3d/models/services/mesh-service';
import { Token } from '@kb3d/models/classes/token';

import type { KbViewer } from '../kb-viewer';
import { CustomMeshNode } from '@kb3d/models/classes/meshes/custom-mesh-node';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { KbQuaternion } from '@kb3d/models/classes/primitives/kb-quaternion';
import { MaterialNode } from '@kb3d/models/classes/materials/material-node';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { KbColor } from '@kb3d/models/classes/primitives/kb-color';
import { Material } from '@babylonjs/core/Materials/material';
import { textureChannelMap } from '@kb3d/view/render/textures/texture';
import { Texture } from '@babylonjs/core/Materials/Textures';
import { eTextureChannel } from '@kb3d/models/classes/enums';
import { FileTextureLayer } from '@kb3d/models/classes';

const babylonTextureProperties = Object.entries(textureChannelMap);

const imageFormatMagicBytes = [
    { magicBytes: [0x89, 0x50, 0x4e, 0x47], extension: 'png' },
    { magicBytes: [0xff, 0xd8], extension: 'jpg' },
    { magicBytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], extension: 'gif' },
    { magicBytes: [0x42, 0x4d], extension: 'bmp' },
];

function getImageFormat(data: Uint8Array): string | null {
    for (const { magicBytes, extension } of imageFormatMagicBytes) {
        let match = true;
        for (let i = 0; i < magicBytes.length; i++) {
            if (data[i] !== magicBytes[i]) {
                match = false;
                break;
            }
        }
        if (match) {
            return extension;
        }
    }
    return null;
}

export class GLTFKb3dImporter {
    constructor(private viewer: KbViewer) {}

    async importGlb(manager: Kb3dManager, files: File[]) {
        const viewer = this.viewer;
        const containers: AssetContainer[] = [];
        for (const file of files) {
            containers.push(await loadGlb(file));
        }

        const meshNodes: CustomMeshNode[] = [];
        const materialNodes: MaterialNode[] = [];
        const textureNodes: FileTextureLayer[] = [];
        const imageDataByUrl = new Map<string, Blob>();

        for (const container of containers) {
            const materialMapping = new Map<Material, MaterialNode>();
            for (const material of container.materials) {
                const materialNode = manager.create(Token.AdvancedMaterialNode);
                materialNode.name = material.name;
                materialNode.alpha = material.alpha;
                materialNode.doubleSided = !material.backFaceCulling;

                if (material instanceof PBRMaterial) {
                    materialNode.albedoColor = KbColor.From(material.albedoColor);
                    materialNode.emissiveColor = KbColor.From(material.emissiveColor);
                    materialNode.ambientColor = KbColor.From(material.ambientColor);
                    materialNode.reflectionColor = KbColor.From(material.reflectionColor);
                    materialNode.metallic = material.metallic || 0;
                    materialNode.roughness = material.roughness || 0;

                    // extract the textures
                    for (const [kb3dProp, bjsProp] of babylonTextureProperties) {
                        if (typeof bjsProp !== 'string') {
                            continue;
                        }
                        if (!(bjsProp in material && material[bjsProp])) {
                            continue;
                        }
                        const texture = material[bjsProp] as Texture;
                        const textureNode = manager.create(Token.FileTextureLayer);
                        if (!texture._buffer) {
                            continue;
                        }
                        let dataUrl = '';
                        let imgBlob: Blob;
                        if (ArrayBuffer.isView(texture._buffer)) {
                            const format = getImageFormat(texture._buffer as Uint8Array) || 'unknown';
                            imgBlob = new Blob([texture._buffer], { type: 'image/' + format });
                        } else if (texture._buffer instanceof Blob) {
                            imgBlob = texture._buffer;
                        } else {
                            console.warn('unknown texture buffer type', texture._buffer);
                            continue;
                        }
                        dataUrl = URL.createObjectURL(imgBlob);
                        imageDataByUrl.set(dataUrl, imgBlob);

                        textureNode.channel = kb3dProp as eTextureChannel;
                        textureNode.name = texture.name || `${material.name}_${kb3dProp}`;
                        textureNode.uOffset = texture.uOffset;
                        textureNode.vOffset = texture.vOffset;
                        textureNode.uScale = texture.uScale;
                        textureNode.vScale = texture.vScale;
                        textureNode.uAngle = (texture.uAng * 180) / Math.PI;
                        textureNode.vAngle = (texture.vAng * 180) / Math.PI;
                        textureNode.wAngle = (texture.wAng * 180) / Math.PI;
                        textureNode.uPivot = texture.uRotationCenter;
                        textureNode.vPivot = texture.vRotationCenter;
                        textureNode.wPivot = texture.wRotationCenter;
                        // This will be a blob url
                        textureNode.path = dataUrl;

                        materialNode.textures.push(textureNode);
                        textureNodes.push(textureNode);
                    }
                } else if (material instanceof StandardMaterial) {
                    materialNode.albedoColor = KbColor.From(material.diffuseColor);
                    materialNode.emissiveColor = KbColor.From(material.emissiveColor);
                    materialNode.ambientColor = KbColor.From(material.ambientColor);
                } else {
                    console.warn('Unsupported material type:', material);
                }
                materialNodes.push(materialNode);
                materialMapping.set(material, materialNode);
            }

            for (const aMesh of container.meshes) {
                if (!(aMesh instanceof Mesh)) {
                    continue;
                }
                const mesh = aMesh;
                if (!mesh.geometry) {
                    continue;
                }

                const newGeometry = new KbGeometry();
                newGeometry.indices = new Uint32Array(mesh.geometry.getIndices() || []);
                newGeometry.positions = new Float32Array(
                    mesh.geometry.getVerticesData(VertexBuffer.PositionKind) || []
                );
                if (newGeometry.indices.length === 0 || newGeometry.positions.length === 0) {
                    return null;
                }
                // flip geometry to match babylonjs coordinate system
                for (let i = 0; i < newGeometry.indices.length; i += 3) {
                    const tmp = newGeometry.indices[i];
                    newGeometry.indices[i] = newGeometry.indices[i + 1];
                    newGeometry.indices[i + 1] = tmp;
                }

                const normals = mesh.geometry.getVerticesData(VertexBuffer.NormalKind);
                if (normals) {
                    newGeometry.normals = new Float32Array(normals);
                }
                const tangents = mesh.geometry.getVerticesData(VertexBuffer.TangentKind);
                if (tangents) {
                    newGeometry.tangents = new Float32Array(tangents);
                }
                const uvs = mesh.geometry.getVerticesData(VertexBuffer.UVKind);
                if (uvs) {
                    newGeometry.uvs = new Float32Array(uvs);
                }
                // Flip V of UVs to match coordinate system
                if (newGeometry.uvs) {
                    for (let i = 1; i < newGeometry.uvs.length; i += 2) {
                        newGeometry.uvs[i] = 1 - newGeometry.uvs[i];
                    }
                }

                const meshNode = manager.create(Token.CustomMeshNode);
                meshNode.name = mesh.name;
                meshNode.position = KbVector.FromVec3(mesh.position);
                if (mesh.rotationQuaternion) {
                    const q = KbQuaternion.FromQuat(mesh.rotationQuaternion);
                    meshNode.rotationAngle = q.getAngle();
                    meshNode.rotationAxis = q.getAxis();
                } else {
                    meshNode.eulerRotation = KbVector.FromVec3(mesh.rotation);
                }
                meshNode.scaling = mesh.scaling.x;
                meshNode.visible = true;
                meshNode._geometry = newGeometry;

                if (mesh.material) {
                    const materialNode = materialMapping.get(mesh.material);
                    if (materialNode) {
                        meshNode.materialId = materialNode.id;
                    }
                }

                meshNodes.push(meshNode);
            }

            // For Testing
            // container.addAllToScene();
        }

        return { meshNodes, materialNodes, textureNodes, imageDataByUrl };

        async function loadGlb(file: File) {
            const fileBlob = await new Promise<Blob>((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = () => {
                    if (!(reader.result instanceof ArrayBuffer)) {
                        reject(new Error('Failed to read file as ArrayBuffer'));
                        return;
                    }
                    const result = reader.result;
                    resolve(new Blob([result]));
                };

                reader.onerror = () => {
                    reject(reader.error);
                };

                reader.readAsArrayBuffer(file);
            });

            const blobUrl = URL.createObjectURL(fileBlob);
            const container = await SceneLoader.LoadAssetContainerAsync(blobUrl, '', viewer.scene, null, '.glb');

            return container;
        }
    }
}
